import { useState, useEffect, useCallback } from 'react';
import './App.css';
import Login from './components/Login';
import ErrorBoundary from './components/ErrorBoundary';
import { Navbar } from './components/Navbar';
import { ConnectionsPanel } from './components/ConnectionsPanel';
import { CyclingTab } from './components/CyclingTab';
import { RunningTab } from './components/RunningTab';
import { Tabs } from './components/Tabs';
import { AdminPanel } from './components/AdminPanel';
import { BarrysTab } from './components/BarrysTab';
import { BookingModal, BookingPrompt } from './components/BookingModal';
import { ToastContainer, ProgressBar } from './components/ui';
import { useToast } from './hooks/useToast';

const APP_NAME = 'FitSync';
const APP_EMOJI = '🚀';

interface User {
  id: string;
  garminEmail: string;
  displayName: string;
  isAdmin: boolean;
}

interface SyncProgress {
  stage: 'idle' | 'dropbox' | 'processing' | 'garmin' | 'complete' | 'error';
  currentFile?: string;
  totalFiles: number;
  processedFiles: number;
  uploadedFiles: number;
  skippedFiles: number;
  errorFiles: number;
  message?: string;
}

interface DropboxStatus {
  connected: boolean;
  accountId?: string;
  email?: string;
  expiresAt?: string;
  createdAt?: string;
}

interface GarminStatus {
  connected: boolean;
  email?: string;
  sessionCreated?: string;
}

interface StravaStatus {
  connected: boolean;
  athleteId?: string;
  firstName?: string;
  lastName?: string;
  createdAt?: string;
}

interface BarrysStatus {
  connected: boolean;
  valid?: boolean;
  email?: string;
  createdAt?: string;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [dropboxStatus, setDropboxStatus] = useState<DropboxStatus | null>(null);
  const [isCheckingDropbox, setIsCheckingDropbox] = useState(false);
  const [garminStatus, setGarminStatus] = useState<GarminStatus | null>(null);
  const [isCheckingGarmin, setIsCheckingGarmin] = useState(false);
  const [stravaStatus, setStravaStatus] = useState<StravaStatus | null>(null);
  const [isCheckingStrava, setIsCheckingStrava] = useState(false);
  const [barrysStatus, setBarrysStatus] = useState<BarrysStatus | null>(null);
  const [isCheckingBarrys, setIsCheckingBarrys] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<SyncProgress>({
    stage: 'idle',
    totalFiles: 0,
    processedFiles: 0,
    uploadedFiles: 0,
    skippedFiles: 0,
    errorFiles: 0,
  });

  const [bookingPrompt, setBookingPrompt] = useState<BookingPrompt | null>(null);
  const [forceTab, setForceTab] = useState<string | null>(null);
  const [barrysFilter, setBarrysFilter] = useState<'all' | 'ariel' | 'myClasses' | undefined>(undefined);

  const { toasts, removeToast, success, error: showError } = useToast();

  const getHeaders = useCallback(() => {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('authToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setIsLoading(false);
      return;
    }

    // Verify token with server (exponential backoff for dev startup when server isn't ready)
    const verifyToken = async (maxRetries = 8) => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          const res = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const user = await res.json();
            setCurrentUser(user);
            setIsAuthenticated(true);
            return;
          }
          // 4xx = genuinely invalid token, don't retry
          if (res.status >= 400 && res.status < 500) {
            localStorage.removeItem('authToken');
            return;
          }
          // 5xx = server/proxy not ready, fall through to retry with delay
        } catch {
          // Network error (server not ready) — fall through to retry with delay
        }
        if (i < maxRetries - 1) {
          await new Promise((r) => setTimeout(r, 500 * Math.pow(2, i)));
        }
      }
      // All retries exhausted — server truly unreachable
      localStorage.removeItem('authToken');
    };
    verifyToken().finally(() => setIsLoading(false));
  }, []);

  // Helper function for API calls
  const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = 3): Promise<Response> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);
        if (response.ok || (response.status >= 400 && response.status < 500)) {
          return response;
        }
        throw new Error(`HTTP ${response.status}`);
      } catch (err) {
        lastError = err as Error;
        if (attempt < maxRetries - 1) {
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError || new Error('All retries failed');
  };

  // Check Dropbox connection status
  const checkDropboxStatus = useCallback(
    async (silent = false): Promise<boolean> => {
      if (!silent) {
        setIsCheckingDropbox(true);
      }
      try {
        const response = await fetchWithRetry('/api/dropbox/status', { headers: getHeaders() });

        if (!response.ok) {
          throw new Error('Failed to fetch Dropbox status');
        }

        const data = await response.json();
        setDropboxStatus(data);
        return data.connected;
      } catch (err) {
        console.error('Error fetching Dropbox status:', err);
        return false;
      } finally {
        if (!silent) {
          setIsCheckingDropbox(false);
        }
      }
    },
    [getHeaders]
  );

  // Connect to Dropbox
  const connectToDropbox = async () => {
    try {
      const response = await fetch('/api/dropbox/auth-url', { headers: getHeaders() });

      if (!response.ok) {
        throw new Error('Failed to get Dropbox auth URL');
      }

      const data = await response.json();
      window.location.href = data.authUrl;
    } catch (err) {
      console.error('Error connecting to Dropbox:', err);
      showError('Failed to connect to Dropbox');
    }
  };

  // Disconnect from Dropbox
  const disconnectFromDropbox = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Dropbox?')) {
      return;
    }

    try {
      const response = await fetch('/api/dropbox/disconnect', {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect from Dropbox');
      }

      setDropboxStatus({ connected: false });
      success('Disconnected from Dropbox');
    } catch (err) {
      console.error('Error disconnecting from Dropbox:', err);
      showError('Failed to disconnect from Dropbox');
    }
  };

  // Check Garmin status
  const checkGarminStatus = useCallback(
    async (silent = false): Promise<boolean> => {
      if (!silent) {
        setIsCheckingGarmin(true);
      }

      try {
        const response = await fetchWithRetry('/api/garmin/status', { headers: getHeaders() });

        if (!response.ok) {
          throw new Error('Failed to fetch Garmin status');
        }

        const data = await response.json();
        setGarminStatus(data);

        // Garmin is the identity provider — if the server can't refresh the
        // session, there's nothing useful the user can do while staying signed
        // in. Clear local auth and let the login screen render so they can
        // sign in again without hunting for the logout button.
        if (data.connected === false && localStorage.getItem('authToken')) {
          localStorage.removeItem('authToken');
          setIsAuthenticated(false);
          setCurrentUser(null);
          showError('Garmin session expired — please log in again.');
        }

        return data.connected;
      } catch (err) {
        console.error('Error fetching Garmin status:', err);
        return false;
      } finally {
        if (!silent) {
          setIsCheckingGarmin(false);
        }
      }
    },
    [getHeaders, showError]
  );

  // Check Strava connection status
  const checkStravaStatus = useCallback(
    async (silent = false): Promise<boolean> => {
      if (!silent) {
        setIsCheckingStrava(true);
      }
      try {
        const response = await fetchWithRetry('/api/strava/status', { headers: getHeaders() });

        if (!response.ok) {
          throw new Error('Failed to fetch Strava status');
        }

        const data = await response.json();
        setStravaStatus(data);
        return data.connected;
      } catch (err) {
        console.error('Error fetching Strava status:', err);
        return false;
      } finally {
        if (!silent) {
          setIsCheckingStrava(false);
        }
      }
    },
    [getHeaders]
  );

  // Connect to Strava
  const connectToStrava = async () => {
    try {
      const response = await fetch('/api/strava/auth-url', { headers: getHeaders() });

      if (!response.ok) {
        throw new Error('Failed to get Strava auth URL');
      }

      const data = await response.json();
      window.location.href = data.authUrl;
    } catch (err) {
      console.error('Error connecting to Strava:', err);
      showError('Failed to connect to Strava');
    }
  };

  // Disconnect from Strava
  const disconnectFromStrava = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Strava?')) {
      return;
    }

    try {
      const response = await fetch('/api/strava/disconnect', {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect from Strava');
      }

      setStravaStatus({ connected: false });
      success('Disconnected from Strava');
    } catch (err) {
      console.error('Error disconnecting from Strava:', err);
      showError('Failed to disconnect from Strava');
    }
  };

  // Check Barry's connection status (admin only)
  const checkBarrysStatus = useCallback(
    async (silent = false): Promise<boolean> => {
      if (!silent) {
        setIsCheckingBarrys(true);
      }
      try {
        const response = await fetchWithRetry('/api/barrys/status', { headers: getHeaders() });
        if (!response.ok) return false;
        const data = await response.json();
        setBarrysStatus(data);
        return data.connected;
      } catch (err) {
        console.error("Error fetching Barry's status:", err);
        return false;
      } finally {
        if (!silent) {
          setIsCheckingBarrys(false);
        }
      }
    },
    [getHeaders]
  );

  // Connect to Barry's (login with credentials)
  const connectToBarrys = async (username: string, password: string) => {
    try {
      const response = await fetch('/api/barrys/connect', {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to connect');
      }

      await checkBarrysStatus();
      success("Connected to Barry's Bootcamp");
    } catch (err: any) {
      showError(err.message || "Failed to connect to Barry's");
      throw err;
    }
  };

  // Disconnect from Barry's
  const disconnectFromBarrys = async () => {
    if (!window.confirm("Are you sure you want to disconnect from Barry's Bootcamp?")) {
      return;
    }

    try {
      const response = await fetch('/api/barrys/disconnect', {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error("Failed to disconnect from Barry's");
      }

      setBarrysStatus({ connected: false });
      success("Disconnected from Barry's Bootcamp");
    } catch (err) {
      console.error("Error disconnecting from Barry's:", err);
      showError("Failed to disconnect from Barry's");
    }
  };

  // Start sync
  const startSync = async (dryRun: boolean) => {
    setIsSyncing(true);
    setSyncProgress({
      stage: 'dropbox',
      totalFiles: 0,
      processedFiles: 0,
      uploadedFiles: 0,
      skippedFiles: 0,
      errorFiles: 0,
      message: 'Starting sync...',
    });

    try {
      const headers = {
        ...getHeaders(),
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      };

      const response = await fetch('/api/garmin/sync', {
        method: 'POST',
        headers,
        body: JSON.stringify({ dryRun }),
      });

      if (!response.ok) {
        throw new Error('Sync failed');
      }

      // Check if we got SSE response
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('text/event-stream')) {
        // Handle SSE streaming
        const reader = response.body?.getReader();
        // eslint-disable-next-line no-undef
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response body');
        }

        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEvent = '';
          let currentData = '';

          for (const line of lines) {
            if (line.startsWith('event:')) {
              currentEvent = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              currentData = line.slice(5).trim();
            } else if (line === '' && currentEvent && currentData) {
              // Complete event received
              try {
                const data = JSON.parse(currentData);

                if (currentEvent === 'progress') {
                  setSyncProgress((prev) => ({
                    ...prev,
                    stage: data.stage,
                    message: data.message,
                    currentFile: data.currentFile,
                    totalFiles: data.totalFiles || prev.totalFiles,
                    uploadedFiles: data.uploadedFiles !== undefined ? data.uploadedFiles : prev.uploadedFiles,
                    skippedFiles: data.skippedFiles !== undefined ? data.skippedFiles : prev.skippedFiles,
                    errorFiles: data.errorFiles !== undefined ? data.errorFiles : prev.errorFiles,
                  }));
                } else if (currentEvent === 'complete') {
                  setSyncProgress({
                    stage: 'complete',
                    totalFiles: data.totalFiles || 0,
                    processedFiles: data.processed || 0,
                    uploadedFiles: data.uploaded || 0,
                    skippedFiles: data.skipped || 0,
                    errorFiles: data.errors || 0,
                    message: dryRun ? 'Dry run complete' : 'Sync complete',
                  });

                  success(dryRun ? 'Dry run complete!' : 'Sync complete!');
                  handleRefresh();
                } else if (currentEvent === 'error') {
                  setSyncProgress((prev) => ({
                    ...prev,
                    stage: 'error',
                    message: data.message || 'Sync failed',
                  }));
                  showError(data.message || 'Sync failed');
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }

              currentEvent = '';
              currentData = '';
            }
          }
        }
      } else {
        // Fallback to regular JSON response
        const result = await response.json();

        setSyncProgress({
          stage: 'complete',
          totalFiles: result.totalFiles || 0,
          processedFiles: result.processed || 0,
          uploadedFiles: result.uploaded || 0,
          skippedFiles: result.skipped || 0,
          errorFiles: result.errors || 0,
          message: dryRun ? 'Dry run complete' : 'Sync complete',
        });

        success(dryRun ? 'Dry run complete!' : 'Sync complete!');
        handleRefresh();
      }
    } catch (error) {
      console.error('Sync error:', error);
      setSyncProgress((prev) => ({
        ...prev,
        stage: 'error',
        message: 'Sync failed. Please try again.',
      }));
      showError('Sync failed. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  // Check connection statuses on mount
  useEffect(() => {
    if (isAuthenticated) {
      checkDropboxStatus();
      checkGarminStatus();
      checkStravaStatus();
      if (currentUser?.isAdmin) {
        checkBarrysStatus();
      }
    }
  }, [
    isAuthenticated,
    currentUser?.isAdmin,
    checkDropboxStatus,
    checkGarminStatus,
    checkStravaStatus,
    checkBarrysStatus,
  ]);

  // Show booking modal: switch to Barry's tab with Ariel filter
  const showBookingPrompt = useCallback((data: BookingPrompt) => {
    setBookingPrompt(data);
    setForceTab('barrys');
    setBarrysFilter('ariel');
  }, []);

  // Handle ?bookPrompt= URL param (set by SW on notification click)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('bookPrompt');
    if (!raw) return;

    window.history.replaceState({}, '', '/');

    try {
      const d = JSON.parse(raw);
      console.log('[App] bookPrompt URL param found, classId:', d.classId);
      if (d.type === 'autobook' && d.classId) {
        showBookingPrompt({
          classId: d.classId,
          classDate: d.classDate,
          dayOfWeek: d.dayOfWeek,
          time: d.time,
          instructor: d.instructor,
          className: d.className,
          dfAvailable: d.dfAvailable,
        });
      }
    } catch (err) {
      console.error('[App] Failed to parse bookPrompt:', err);
    }
  }, [showBookingPrompt]);

  // Booking modal actions
  const handleBookConfirm = useCallback(async () => {
    if (!bookingPrompt) return;
    const headers = { ...getHeaders(), 'Content-Type': 'application/json' };

    const res = await fetch('/api/barrys/book', {
      method: 'POST',
      headers,
      body: JSON.stringify({ classId: bookingPrompt.classId }),
    });
    const result = await res.json();

    if (!result.success) throw new Error(result.message || 'Booking failed');

    success(`Booked DF spot: ${result.spot}`);
    fetch('/api/barrys/booking-decision', {
      method: 'POST',
      headers,
      body: JSON.stringify({ classId: bookingPrompt.classId, decision: 'booked' }),
    }).catch((err) => console.error('Failed to save booking decision:', err));

    // Sync to update cached schedule before closing modal
    await fetch('/api/barrys/sync', { method: 'POST', headers: getHeaders() });

    setBookingPrompt(null);
    setRefreshTrigger((prev) => prev + 1);
  }, [bookingPrompt, getHeaders, success]);

  const handleBookSkip = useCallback(() => {
    if (!bookingPrompt) return;
    const headers = { ...getHeaders(), 'Content-Type': 'application/json' };

    fetch('/api/barrys/booking-decision', {
      method: 'POST',
      headers,
      body: JSON.stringify({ classId: bookingPrompt.classId, decision: 'declined' }),
    }).catch((err) => console.error('Failed to save skip decision:', err));

    setBookingPrompt(null);
  }, [bookingPrompt, getHeaders]);

  // Test: fetch a bookable class and navigate with the same URL param the SW uses
  const handleTestNotification = useCallback(async () => {
    try {
      const res = await fetch('/api/barrys/test-notification', { headers: getHeaders() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const payload = { type: 'autobook', ...data };
      window.location.href = `/?bookPrompt=${encodeURIComponent(JSON.stringify(payload))}`;
    } catch (err: any) {
      showError(err.message || 'No bookable classes found');
    }
  }, [getHeaders, showError]);

  // Handle successful login
  const handleLogin = (_token: string, user: User) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
    checkDropboxStatus(true);
    checkGarminStatus(true);
    checkStravaStatus(true);
    if (currentUser?.isAdmin) {
      checkBarrysStatus(true);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="app">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <ErrorBoundary>
        <Login onLoginSuccess={handleLogin} />
      </ErrorBoundary>
    );
  }

  // Garmin is always connected (user logged in with Garmin).
  // Dropbox + Strava needed for full sync. Barry's needed for admin users.
  const isConnected =
    dropboxStatus?.connected === true &&
    garminStatus?.connected === true &&
    stravaStatus?.connected === true &&
    (!currentUser?.isAdmin || barrysStatus?.connected === true);

  // Main app content
  return (
    <ErrorBoundary>
      <div className="app">
        {/* Toast Notifications */}
        <ToastContainer toasts={toasts} onClose={removeToast} />

        {/* Booking Modal (from push notification) */}
        {bookingPrompt && (
          <BookingModal
            prompt={bookingPrompt}
            onBook={handleBookConfirm}
            onSkip={handleBookSkip}
            onDismiss={() => setBookingPrompt(null)}
          />
        )}

        {/* Navbar */}
        <Navbar
          appName={APP_NAME}
          appEmoji={APP_EMOJI}
          dropboxStatus={dropboxStatus}
          garminStatus={garminStatus}
          stravaStatus={stravaStatus}
          barrysStatus={currentUser?.isAdmin ? barrysStatus : undefined}
          isCheckingDropbox={isCheckingDropbox}
          isCheckingGarmin={isCheckingGarmin}
          isCheckingStrava={isCheckingStrava}
          isCheckingBarrys={isCheckingBarrys}
          currentUser={currentUser}
          onLogout={handleLogout}
          onDisconnectDropbox={disconnectFromDropbox}
          onDisconnectStrava={disconnectFromStrava}
          onDisconnectBarrys={disconnectFromBarrys}
          onError={showError}
          onTestNotification={import.meta.env.DEV && currentUser?.isAdmin ? handleTestNotification : undefined}
        />

        {/* Sync Progress Bar (shown when syncing) */}
        {isSyncing && syncProgress.stage !== 'idle' && (
          <div className="sync-progress-bar">
            <div className="sync-progress-bar__content">
              <div className="sync-progress-bar__status">
                <p className="sync-progress-bar__message">
                  {syncProgress.stage === 'dropbox' && '📁 Syncing files from Dropbox...'}
                  {syncProgress.stage === 'processing' && '⚙️ Processing files...'}
                  {syncProgress.stage === 'garmin' && '🏃 Uploading to Garmin Connect...'}
                  {syncProgress.stage === 'complete' && '✓ Sync complete!'}
                  {syncProgress.stage === 'error' && '✕ Sync failed'}
                </p>
                {syncProgress.currentFile && (
                  <p className="sync-progress-bar__detail">Processing: {syncProgress.currentFile}</p>
                )}
              </div>
              {syncProgress.totalFiles > 0 && (
                <ProgressBar
                  current={syncProgress.uploadedFiles + syncProgress.skippedFiles + syncProgress.errorFiles}
                  total={syncProgress.totalFiles}
                  label=""
                  color={
                    syncProgress.stage === 'complete' ? 'green' : syncProgress.stage === 'error' ? 'orange' : 'blue'
                  }
                />
              )}
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="app-main">
          <div className="content-container">
            {/* Connections Panel - Only show when not fully connected */}
            {!isConnected && (
              <section className="app-section">
                <ConnectionsPanel
                  dropboxStatus={dropboxStatus}
                  garminStatus={garminStatus}
                  stravaStatus={stravaStatus}
                  barrysStatus={currentUser?.isAdmin ? barrysStatus : undefined}
                  isCheckingDropbox={isCheckingDropbox}
                  isCheckingGarmin={isCheckingGarmin}
                  isCheckingStrava={isCheckingStrava}
                  isCheckingBarrys={isCheckingBarrys}
                  onConnectDropbox={connectToDropbox}
                  onDisconnectDropbox={disconnectFromDropbox}
                  onConnectStrava={connectToStrava}
                  onDisconnectStrava={disconnectFromStrava}
                  onConnectBarrys={connectToBarrys}
                  onDisconnectBarrys={disconnectFromBarrys}
                />
              </section>
            )}

            {/* Tabs for Running, Cycling, and Admin */}
            <section className="app-section">
              <Tabs
                tabs={[
                  {
                    id: 'cycling',
                    label: '🚴 Cycling',
                    content: (
                      <CyclingTab
                        refreshTrigger={refreshTrigger}
                        isConnected={isConnected}
                        isSyncing={isSyncing}
                        onDryRun={() => startSync(true)}
                        onSync={() => startSync(false)}
                      />
                    ),
                  },
                  {
                    id: 'running',
                    label: '🏃 Running',
                    content: (
                      <RunningTab
                        refreshTrigger={refreshTrigger}
                        isConnected={isConnected}
                        isSyncing={isSyncing}
                        onDryRun={() => {}}
                        onSync={() => {}}
                      />
                    ),
                  },
                  ...(currentUser?.isAdmin
                    ? [
                        {
                          id: 'barrys',
                          label: "💪 Barry's",
                          content: <BarrysTab refreshTrigger={refreshTrigger} initialFilter={barrysFilter} />,
                        },
                        {
                          id: 'admin',
                          label: '⚙️ Admin',
                          content: <AdminPanel />,
                        },
                      ]
                    : []),
                ]}
                defaultTab="cycling"
                forceTab={forceTab}
              />
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer className="app-footer">
          <p>Built with ❤️ using React, TypeScript, Vite, and Express — v{import.meta.env.VITE_APP_VERSION || 'dev'}</p>
        </footer>
      </div>
    </ErrorBoundary>
  );
}

export default App;
